import { useEffect, useState } from "react";

const ONETRUST_PERFORMANCE_GROUP_ID = "C0002";
const ONETRUST_PERSONALIZATION_GROUP_ID = "C0003";
// C0004 is marketing/advertising and is intentionally unused.

type OneTrustGroupsUpdatedEvent = Event & {
  detail?: string[];
};

type OneTrustCookieConsentListener = (groups: string[]) => void;

const oneTrustCookieConsentListeners = new Set<OneTrustCookieConsentListener>();
let oneTrustCookieConsentSubscriptionInitialized = false;

declare global {
  interface Window {
    OneTrust?: {
      OnConsentChanged?: (callback: () => void) => void;
    };
    OnetrustActiveGroups?: string;
    OptanonActiveGroups?: string;
  }
}

export function readActiveOneTrustGroups(): string[] {
  if (typeof window === "undefined") return [];

  const activeGroups =
    window.OnetrustActiveGroups || window.OptanonActiveGroups || "";

  return activeGroups
    .split(",")
    .map((group) => group.trim())
    .filter(Boolean);
}

export function hasPerformanceCookieConsent(
  groups: string[] = readActiveOneTrustGroups(),
): boolean {
  return groups.includes(ONETRUST_PERFORMANCE_GROUP_ID);
}

export function hasPersonalizationCookieConsent(
  groups: string[] = readActiveOneTrustGroups(),
): boolean {
  return groups.includes(ONETRUST_PERSONALIZATION_GROUP_ID);
}

export function hasPostHogPersistenceConsent(
  groups: string[] = readActiveOneTrustGroups(),
): boolean {
  return (
    hasPerformanceCookieConsent(groups) ||
    hasPersonalizationCookieConsent(groups)
  );
}

function getCookieConsentGroupsFromEvent(event: Event): string[] {
  const groups = (event as OneTrustGroupsUpdatedEvent).detail;
  return Array.isArray(groups) ? groups : readActiveOneTrustGroups();
}

function notifyOneTrustCookieConsentListeners(
  groups: string[] = readActiveOneTrustGroups(),
): void {
  oneTrustCookieConsentListeners.forEach((listener) => {
    listener(groups);
  });
}

function initializeOneTrustCookieConsentSubscription(): void {
  if (
    oneTrustCookieConsentSubscriptionInitialized ||
    typeof window === "undefined"
  ) {
    return;
  }
  oneTrustCookieConsentSubscriptionInitialized = true;

  const handleGroupsUpdated = (event: Event) => {
    notifyOneTrustCookieConsentListeners(
      getCookieConsentGroupsFromEvent(event),
    );
  };
  const handleCookieConsentChanged = () => {
    notifyOneTrustCookieConsentListeners();
  };

  window.addEventListener("OneTrustGroupsUpdated", handleGroupsUpdated);
  window.addEventListener("bannerLoaded", handleCookieConsentChanged);
  window.OneTrust?.OnConsentChanged?.(handleCookieConsentChanged);
}

export function subscribeToOneTrustCookieConsentChanges(
  listener: OneTrustCookieConsentListener,
): () => void {
  if (typeof window === "undefined") return () => {};

  initializeOneTrustCookieConsentSubscription();
  oneTrustCookieConsentListeners.add(listener);

  return () => {
    oneTrustCookieConsentListeners.delete(listener);
  };
}

export function usePerformanceCookieConsent(): boolean {
  const [hasCookieConsent, setHasCookieConsent] = useState(
    hasPerformanceCookieConsent,
  );

  useEffect(() => {
    return subscribeToOneTrustCookieConsentChanges((groups) => {
      setHasCookieConsent(hasPerformanceCookieConsent(groups));
    });
  }, []);

  return hasCookieConsent;
}

export function usePersonalizationCookieConsent(): boolean {
  const [hasCookieConsent, setHasCookieConsent] = useState(
    hasPersonalizationCookieConsent,
  );

  useEffect(() => {
    return subscribeToOneTrustCookieConsentChanges((groups) => {
      setHasCookieConsent(hasPersonalizationCookieConsent(groups));
    });
  }, []);

  return hasCookieConsent;
}
